import React from "react";
import "../styles/PrintPage.css";
import { Box, Button, IconPrint, Text, Alert, Stack } from "braid-design-system";
import { useLocation } from "react-router-dom";
import { getPlAccount, getPlAccountPrintStatus, markPlAccountPrinted, PlAccountResponse } from "../services/plAccountService";
import { CustomerResponse } from "../services/approveMobileCustomerService";
import apiClient from "../clients/apiClient";
import Loading from "../components/Loading";
import { formatCurrency } from "../utils/currencyUtils";
import { formatPrintDate } from "../utils/dateUtils";
import { useToast } from "../hooks/useToast";
import { getPrintConfig } from "../services/transactionService";
import { numberToWords } from "../utils/numberToWords";
import { getFieldLabel } from "../utils/printLabels";
import { openPrintWindowHTML } from "../utils/printUtils";

interface PawningMandatePrintProps {
    accountId?: number;
}

const PawningMandatePrint: React.FC<PawningMandatePrintProps> = ({ accountId: propAccountId }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const accountId = propAccountId || Number(queryParams.get('accountId'));

    const [loading, setLoading] = React.useState(true);
    const [printing, setPrinting] = React.useState(false);
    const [account, setAccount] = React.useState<PlAccountResponse | null>(null);
    const [customer, setCustomer] = React.useState<CustomerResponse | null>(null);
    const [printConfig, setPrintConfig] = React.useState<any>(null);
    const [printConfigError, setPrintConfigError] = React.useState<string>("");
    const [isPrinted, setIsPrinted] = React.useState(false);
    const [subCategories, setSubCategories] = React.useState<string[]>([]);
    const [selectedSubCategory, setSelectedSubCategory] = React.useState<string>("");
    const [fullLoadedConfig, setFullLoadedConfig] = React.useState<any>(null);

    const toast = useToast();
    const [printLang, setPrintLang] = React.useState('si');

    // Dynamic / custom info fields loaded from backend before printing
    const [customerInfos, setCustomerInfos] = React.useState<any[]>([]);
    const [accountInfos, setAccountInfos] = React.useState<any[]>([]);

    const getCustomerById = async (customerId: number): Promise<CustomerResponse | null> => {
        try {
            const response = await apiClient.get(`/customer/id/${customerId}`);
            return response.data.data || null;
        } catch {
            return null;
        }
    };

    React.useEffect(() => {
        const fetchData = async () => {
            if (!accountId) return;
            setLoading(true);
            try {
                const [accountData, config, printStatusRes] = await Promise.all([
                    getPlAccount(accountId),
                    getPrintConfig(accountId, 'PAWNING_MANDATE'),
                    getPlAccountPrintStatus(accountId).catch(() => null),
                ]);

                setAccount(accountData);

                // Set print status (printed=1 means it has been printed before → reprint)
                if (printStatusRes?.printed === 1) {
                    setIsPrinted(true);
                }

                if (accountData?.ci_customer_id) {
                    const customerData = await getCustomerById(accountData.ci_customer_id);
                    setCustomer(customerData);
                }

                // Fetch customer custom fields and account custom fields
                let customerInfoData: any[] = [];
                let accountInfoData: any[] = [];

                if (accountData?.ci_customer_id) {
                    try {
                        const infoRes = await apiClient.get(`/customer/${accountData.ci_customer_id}/info`);
                        customerInfoData = infoRes.data?.data || [];
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error("Error fetching customer info fields:", err);
                    }
                }

                if (accountId) {
                    try {
                        const infoRes = await apiClient.get(`/loan-application/account_info/${accountId}`);
                        accountInfoData = infoRes.data?.data || [];
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error("Error fetching account info fields:", err);
                    }
                }

                setCustomerInfos(customerInfoData);
                setAccountInfos(accountInfoData);

                if (config && typeof config === 'object' && Object.keys(config).length > 0) {
                    setFullLoadedConfig(config);
                    let resolvedCfg = config;
                    if (Array.isArray(config.sub_sections)) {
                        const subKeys = config.sub_sections.map((obj: any) => Object.keys(obj)[0]).filter(Boolean);
                        setSubCategories(subKeys);
                        setSelectedSubCategory("");
                        // Strip sub_sections to get default config
                        const defaultCfg = { ...config };
                        delete defaultCfg.sub_sections;
                        resolvedCfg = defaultCfg;
                    } else {
                        setSubCategories([]);
                        setSelectedSubCategory("");
                    }
                    setPrintConfig(resolvedCfg);
                    setPrintLang(resolvedCfg.default_lang || "en");
                    setPrintConfigError("");
                } else {
                    setPrintConfig({});
                    setPrintConfigError("No Pawning Mandate print configuration found. Please set it up in Print Policy Config.");
                }
            } catch (error: any) {
                toast.showError(`Error loading Pawning Mandate data: ${error.message}`);
                setPrintConfig({});
                setPrintConfigError(`Failed to load print config: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [accountId]);

    // Handle sub-category switch
    const handleSubCategoryChange = (name: string) => {
        setSelectedSubCategory(name);
        if (!fullLoadedConfig) return;
        if (name) {
            const foundObj = (fullLoadedConfig.sub_sections || []).find((obj: any) => Object.keys(obj)[0] === name);
            setPrintConfig(foundObj ? foundObj[name] : {});
            const defaultLanguage = (foundObj ? foundObj[name] : {})?.default_lang || fullLoadedConfig.default_lang || "en";
            setPrintLang(defaultLanguage);
        } else {
            const defaultCfg = { ...fullLoadedConfig };
            delete defaultCfg.sub_sections;
            setPrintConfig(defaultCfg);
            setPrintLang(defaultCfg.default_lang || "en");
        }
    };

    const formatDate = (date: string | Date | null | undefined): string => {
        if (!date) return '';
        if (printConfig?.date_format) {
            return formatPrintDate(date, printConfig.date_format);
        }
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getLocalizedValue = (obj: any, baseKey: string, lang?: string, fieldId?: string) => {
        if (!obj) return '';
        const currentLang = lang || printLang || localStorage.getItem('app_lang') || 'en';

        let isTranslate = true;
        if (fieldId && printConfig && printConfig[fieldId] && printConfig[fieldId].translate !== undefined) {
            isTranslate = Boolean(printConfig[fieldId].translate);
        } else if (fieldId) {
            const cleanKey = fieldId.replace(/_copy_\d+$/, "");
            if (printConfig && printConfig[cleanKey] && printConfig[cleanKey].translate !== undefined) {
                isTranslate = Boolean(printConfig[cleanKey].translate);
            }
        }

        const cleanLang = isTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
        if (cleanLang === "si" && obj[`${baseKey}_ln2`]?.trim()) return obj[`${baseKey}_ln2`].trim();
        if (cleanLang === "ta" && obj[`${baseKey}_ln3`]?.trim()) return obj[`${baseKey}_ln3`].trim();
        const fallback = obj[`${baseKey}_ln1`] || obj[baseKey];
        return typeof fallback === 'string' ? fallback.trim() : String(fallback ?? '');
    };

    const getFieldValue = (field: string, reprinting?: boolean, itemIndex?: number, originalField?: string): string => {
        if (!account && !customer) return '';
        const currentLang = printLang || localStorage.getItem('app_lang') || 'en';
        const activeField = originalField || field;

        // Handle copy fields (e.g., balance_copy_1 -> balance)
        const copyMatch = field.match(/^(.+)_copy_\d+$/);
        if (copyMatch) {
            const baseField = copyMatch[1];
            return getFieldValue(baseField, reprinting, itemIndex, field);
        }

        // Handle pawning item fields with index (e.g., quantity_1, quantity_2)
        const itemMatch = field.match(/^(.+)_(\d+)$/);
        if (itemMatch && ['name', 'quantity', 'carat', 'weight_gram', 'gold_weight_gram', 'accessed_value', 'accessed_value_words', 'market_value', 'market_value_words', 'description'].includes(itemMatch[1])) {
            const baseField = itemMatch[1];
            const index = parseInt(itemMatch[2]) - 1; // Convert to 0-based index
            return getFieldValue(baseField, reprinting, index, originalField);
        }

        switch (field) {
            case 'account_number': return account?.account_number || '';
            case 'c_by': return (account as any)?.c_by != null ? String((account as any).c_by) : '';
            case 'c_by_name': return (account as any)?.c_by_name || '';
            case 'voucher_number': return (account as any)?.voucher_number || '';
            case 'customer_number': return customer?.customer_number || '';
            case 'account_type_name': return getLocalizedValue(account?.account_type, 'name', currentLang, activeField) || account?.product_name || account?.account_type?.name_ln1 || '';
            case 'customer_address':
                return getLocalizedValue(customer, 'address', currentLang, activeField);
            case 'title':
                return getLocalizedValue(customer, 'title', currentLang, activeField);
            case 'full_name':
                return getLocalizedValue(customer, 'full_name', currentLang, activeField);
            case 'ini_name':
                return getLocalizedValue(customer, 'ini_name', currentLang, activeField);
            case 'address':
                return getLocalizedValue(customer, 'address', currentLang, activeField);
            case 'address_ln1':
            case 'address_ln2':
            case 'address_ln3': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.address?.translate !== undefined
                        ? Boolean(printConfig.address.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                if (cleanLang === "si") return (customer as any)?.address_ln2 || customer?.address_ln1 || '';
                if (cleanLang === "ta") return (customer as any)?.address_ln3 || customer?.address_ln1 || '';
                return customer?.address_ln1 || '';
            }
            case 'customer_mobile_number':
            case 'mobile_1': return customer?.mobile_1 || '';
            case 'customer_nic':
            case 'nic': return customer?.nic || '';
            case 'open_date': return formatDate(account?.open_date);
            case 'reneve_date':
            case 'renew_date':
            case 'maturity_date':
            case 'closing_date':
                return (account as any)?.reneve_date ? formatDate((account as any).reneve_date) : '';
            case 'open_time': {
                if (account?.open_date) {
                    const d = new Date(account.open_date);
                    if (!isNaN(d.getTime())) {
                        return d.toTimeString().split(' ')[0];
                    }
                }
                return '';
            }
            case 'interest_rate_by_12':
            case 'interest_rate/12': {
                const rate = account?.interest_rate != null ? Number(account.interest_rate) : NaN;
                return isNaN(rate) ? '' : (rate / 12).toFixed(2);
            }
            case 'full_name_ln1':
            case 'full_name_ln2':
            case 'full_name_ln3': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.full_name?.translate !== undefined
                        ? Boolean(printConfig.full_name.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                if (cleanLang === "si") return (customer as any)?.full_name_ln2 || (customer as any)?.full_name_ln1 || '';
                if (cleanLang === "ta") return (customer as any)?.full_name_ln3 || (customer as any)?.full_name_ln1 || '';
                return (customer as any)?.full_name_ln1 || '';
            }
            case 'ini_name_ln1':
            case 'ini_name_ln2':
            case 'ini_name_ln3': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.ini_name?.translate !== undefined
                        ? Boolean(printConfig.ini_name.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                if (cleanLang === "si") return (customer as any)?.ini_name_ln2 || (customer as any)?.ini_name_ln1 || '';
                if (cleanLang === "ta") return (customer as any)?.ini_name_ln3 || (customer as any)?.ini_name_ln1 || '';
                return (customer as any)?.ini_name_ln1 || '';
            }
            case 'balance': return account?.balance != null ? formatCurrency(account.balance) : '';
            case 'balance_words': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.balance_words?.translate !== undefined
                        ? Boolean(printConfig.balance_words.translate)
                        : true;
                return account?.balance != null ? numberToWords(account.balance, isFieldTranslate ? currentLang : "en") : '';
            }
            case 'ref_account_number': return (account as any)?.ref_account_number || '';
            case 'note': return (account as any)?.note || '';
            case 'capital':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    const item = (account as any).pawning_items[itemIndex];
                    return item.capital != null ? formatCurrency(item.capital) : '';
                }
                return (account as any)?.capital != null ? formatCurrency((account as any).capital) : '';
            case 'capital_words': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.capital_words?.translate !== undefined
                        ? Boolean(printConfig.capital_words.translate)
                        : true;
                return (account as any)?.capital != null ? numberToWords((account as any).capital, isFieldTranslate ? currentLang : "en") : '';
            }
            case 'interest': return (account as any)?.interest != null ? formatCurrency((account as any).interest) : '';
            case 'interest_words': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.interest_words?.translate !== undefined
                        ? Boolean(printConfig.interest_words.translate)
                        : true;
                return (account as any)?.interest != null ? numberToWords((account as any).interest, isFieldTranslate ? currentLang : "en") : '';
            }
            case 'interest_rate': {
                const isHidePercent = printConfig?.[activeField]?.hide_percent === true || printConfig?.[activeField]?.hide_percent === 1 || printConfig?.[activeField]?.hide_percent === "true" ||
                                      printConfig?.interest_rate?.hide_percent === true || printConfig?.interest_rate?.hide_percent === 1 || printConfig?.interest_rate?.hide_percent === "true";
                return account?.interest_rate ? (isHidePercent ? `${account.interest_rate}` : `${account.interest_rate}%`) : '';
            }
            case 'period': {
                const isHideMonths = printConfig?.[activeField]?.hide_months === true || printConfig?.[activeField]?.hide_months === 1 || printConfig?.[activeField]?.hide_months === "true" ||
                    printConfig?.period?.hide_months === true || printConfig?.period?.hide_months === 1 || printConfig?.period?.hide_months === "true";
                if (isHideMonths) return account?.period ? String(account.period) : '';
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.period?.translate !== undefined
                        ? Boolean(printConfig.period.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                return cleanLang === 'si' ? `මාස ${account?.period || ''}` : cleanLang === 'ta' ? `மாதங்கள் ${account?.period || ''}` : `${account?.period || ''} Months`;
            }
            case 'period_minus_one': {
                const periodVal = account?.period ? parseInt(String(account.period), 10) : 0;
                const calculatedPeriod = periodVal > 0 ? periodVal - 1 : '';
                if (!calculatedPeriod) return '';
                const isHideMonths = printConfig?.[activeField]?.hide_months === true || printConfig?.[activeField]?.hide_months === 1 || printConfig?.[activeField]?.hide_months === "true" ||
                    printConfig?.period?.hide_months === true || printConfig?.period?.hide_months === 1 || printConfig?.period?.hide_months === "true";
                if (isHideMonths) return String(calculatedPeriod);
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.period?.translate !== undefined
                        ? Boolean(printConfig.period.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                return cleanLang === 'si' ? `මාස ${calculatedPeriod}` : cleanLang === 'ta' ? `மாதங்கள் ${calculatedPeriod}` : `${calculatedPeriod} Months`;
            }

            // Pawning Specific fields
            case 'description':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].description || '';
                }
                return (account as any)?.pawning_items?.[0]?.description || '';
            case 'name':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    const item = (account as any).pawning_items[itemIndex];
                    return getLocalizedValue(item?.pawning_item_type, 'name', currentLang, activeField) || item?.pawning_item_type?.name_ln1 || '';
                }
                const firstItem = (account as any)?.pawning_items?.[0];
                return getLocalizedValue(firstItem?.pawning_item_type, 'name', currentLang, activeField) || firstItem?.pawning_item_type?.name_ln1 || '';
            case 'quantity':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].quantity?.toString() || '';
                }
                return (account as any)?.pawning_items?.[0]?.quantity?.toString() || '';
            case 'carat':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].carat || '';
                }
                return (account as any)?.pawning_items?.[0]?.carat || '';
            case 'weight_gram':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].weight_gram?.toString() || '';
                }
                return (account as any)?.pawning_items?.[0]?.weight_gram?.toString() || '';
            case 'gold_weight_gram':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].gold_weight_gram?.toString() || '';
                }
                return (account as any)?.pawning_items?.[0]?.gold_weight_gram?.toString() || '';
            case 'accessed_value':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].accessed_value != null ? formatCurrency((account as any).pawning_items[itemIndex].accessed_value) : '';
                }
                return (account as any)?.pawning_items?.[0]?.accessed_value != null ? formatCurrency((account as any).pawning_items[0].accessed_value) : '';
            case 'accessed_value_words': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.accessed_value_words?.translate !== undefined
                        ? Boolean(printConfig.accessed_value_words.translate)
                        : true;
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].accessed_value != null ? numberToWords((account as any).pawning_items[itemIndex].accessed_value, isFieldTranslate ? currentLang : "en") : '';
                }
                return (account as any)?.pawning_items?.[0]?.accessed_value != null ? numberToWords((account as any).pawning_items[0].accessed_value, isFieldTranslate ? currentLang : "en") : '';
            }
            case 'market_value':
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].market_value != null ? formatCurrency((account as any).pawning_items[itemIndex].market_value) : '';
                }
                return (account as any)?.pawning_items?.[0]?.market_value != null ? formatCurrency((account as any).pawning_items[0].market_value) : '';
            case 'market_value_words': {
                const isFieldTranslate = printConfig?.[activeField]?.translate !== undefined
                    ? Boolean(printConfig[activeField].translate)
                    : printConfig?.market_value_words?.translate !== undefined
                        ? Boolean(printConfig.market_value_words.translate)
                        : true;
                if (itemIndex !== undefined && (account as any)?.pawning_items?.[itemIndex]) {
                    return (account as any).pawning_items[itemIndex].market_value != null ? numberToWords((account as any).pawning_items[itemIndex].market_value, isFieldTranslate ? currentLang : "en") : '';
                }
                return (account as any)?.pawning_items?.[0]?.market_value != null ? numberToWords((account as any).pawning_items[0].market_value, isFieldTranslate ? currentLang : "en") : '';
            }

            // Aggregated fields
            case 'gold_weight_gram2': return (account as any)?.gold_weight_gram2?.toString() || '';
            case 'market_value2': return (account as any)?.market_value2 != null ? formatCurrency((account as any).market_value2) : '';
            case 'accessed_value2': return (account as any)?.accessed_value2 != null ? formatCurrency((account as any).accessed_value2) : '';

            // Total fields for pawning columns
            case 'total_weight_gram': {
                const items = (account as any)?.pawning_items;
                if (!items || !items.length) return '';
                const total = items.reduce((sum: number, item: any) => sum + (Number(item.weight_gram) || 0), 0);
                return total.toFixed(2);
            }
            case 'total_gold_weight_gram': {
                const items = (account as any)?.pawning_items;
                if (!items || !items.length) return '';
                const total = items.reduce((sum: number, item: any) => sum + (Number(item.gold_weight_gram) || 0), 0);
                return total.toFixed(2);
            }
            case 'total_accessed_value': {
                const items = (account as any)?.pawning_items;
                if (!items || !items.length) return '';
                const total = items.reduce((sum: number, item: any) => sum + (Number(item.accessed_value) || 0), 0);
                return formatCurrency(total);
            }
            case 'total_market_value': {
                const items = (account as any)?.pawning_items;
                if (!items || !items.length) return '';
                const total = items.reduce((sum: number, item: any) => sum + (Number(item.market_value) || 0), 0);
                return formatCurrency(total);
            }

            case 'carat_grouped': {
                const items = (account as any)?.pawning_items;
                if (!items || !items.length) return '';
                const grouped: Record<string, { count: number; weight: number; goldWeight: number; assessedValue: number }> = {};
                items.forEach((item: any) => {
                    const carat = String(item.carat || '0');
                    if (!grouped[carat]) grouped[carat] = { count: 0, weight: 0, goldWeight: 0, assessedValue: 0 };
                    grouped[carat].count += 1;
                    grouped[carat].weight += Number(item.weight_gram) || 0;
                    grouped[carat].goldWeight += Number(item.gold_weight_gram) || 0;
                    grouped[carat].assessedValue += Number(item.accessed_value) || 0;
                });
                return Object.entries(grouped)
                    .map(([carat, g]) => `${carat}K: ${g.count} items | ${g.weight.toFixed(2)}g | ${g.goldWeight.toFixed(2)}g | ${formatCurrency(g.assessedValue)}`)
                    .join('\n');
            }

            case 'carat_grouped_total': {
                const items = (account as any)?.pawning_items;
                if (!items || !items.length) return '';
                const grouped: Record<string, number> = {};
                items.forEach((item: any) => {
                    const carat = String(item.carat || '0');
                    grouped[carat] = (grouped[carat] || 0) + 1;
                });
                return Object.entries(grouped)
                    .map(([carat, count]) => `${carat}K: ${count} item${count > 1 ? 's' : ''}`)
                    .join(', ');
            }

            case 'print_time':
                return formatPrintDate(new Date(), printConfig?.time_format || 'HH:mm:ss');
            case 'print_date':
                return formatPrintDate(new Date(), printConfig?.date_format);
            case 'reprint':
                // Only show RE-PRINT text if this is actually a reprint (printed flag was already set)
                return reprinting ? `RE-PRINT` : '';
            default: {
                const baseField = field.replace(/_copy_\d+$/, "");
                if (baseField === 'institute_name') {
                    return getLocalizedValue((account?.branch as any)?.institute, 'name', currentLang, activeField) || getLocalizedValue(account, 'institute_name', currentLang, activeField) || '';
                }
                if (baseField === 'institute_address') {
                    return getLocalizedValue((account?.branch as any)?.institute, 'address', currentLang, activeField) || (account as any)?.institute_address || '';
                }
                if (baseField === 'institute_email') {
                    return (account?.branch as any)?.institute?.email || (account as any)?.institute_email || '';
                }
                if (baseField === 'institute_reg_num') {
                    return (account?.branch as any)?.institute?.registration_number || (account?.branch as any)?.institute?.reg_num || (account as any)?.institute_reg_num || '';
                }
                if (baseField === 'institute_reg_date') {
                    const rawDate = (account?.branch as any)?.institute?.registration_date || (account?.branch as any)?.institute?.reg_date || (account as any)?.institute_reg_date || '';
                    return formatPrintDate(rawDate, printConfig?.date_format);
                }
                if (baseField === 'branch_name') {
                    return getLocalizedValue(account?.branch, 'name', currentLang, activeField);
                }
                if (baseField === 'branch_address') {
                    return getLocalizedValue(account?.branch, 'address', currentLang, activeField);
                }
                if (baseField === 'branch_email') {
                    return account?.branch?.e_mail || (account as any)?.branch_email || '';
                }
                if (baseField === 'institute_phone_number' || baseField === 'institute_phone') {
                    return (account?.branch as any)?.institute?.phone || '';
                }
                if (baseField === 'branch_telephone_number' || baseField === 'branch_telephone') {
                    return account?.branch?.telephone || '';
                }
                if (baseField === 'member_number') {
                    return customer?.customer_number || '';
                }
                if (baseField === 'mobile_number') {
                    return customer?.mobile_1 || customer?.mobile_2 || '';
                }

                // Check customer custom fields
                if (customerInfos.length > 0) {
                    const matchCust = customerInfos.find(info => {
                        const infoName = (info.field_name || '').toLowerCase().replace(/\s+/g, '_');
                        const targetName = baseField.toLowerCase().replace(/\s+/g, '_');
                        return infoName === targetName || info.field_name === baseField;
                    });
                    if (matchCust) return matchCust.value || '';
                }

                // Check account custom fields
                if (accountInfos.length > 0) {
                    const matchAcc = accountInfos.find(info => {
                        const infoName = (info.pl_account_type_info?.field_name || '').toLowerCase().replace(/\s+/g, '_');
                        const targetName = baseField.toLowerCase().replace(/\s+/g, '_');
                        return infoName === targetName || info.pl_account_type_info?.field_name === baseField;
                    });
                    if (matchAcc) return matchAcc.information || '';
                }

                if (account && field in account) {
                    const val = (account as any)[field];
                    if (val instanceof Date || (typeof val === 'string' && !isNaN(Date.parse(val)) && (val.includes('-') || val.includes('/')))) {
                        return formatDate(val);
                    }
                    if (typeof val === 'number') {
                        if (field.endsWith('rate') || field.includes('percent')) {
                            return `${val}%`;
                        }
                        const moneyFields = ['balance', 'capital', 'interest', 'approved', 'minimum_balance', 'lowest_balance', 'penalty', 'fund', 'insurance', 'fee', 'past_due_amount', 'standing_order_amount', 'tax', 'opening_balance', 'capital_installment', 'total_installment', 'save_interest_temp', 'due_interest', 'interest_installment', 'interest_balance', 'total_interest'];
                        if (moneyFields.includes(field)) {
                            return formatCurrency(val);
                        }
                        return String(val);
                    }
                    return String(val ?? '');
                }
                if (customer && field in customer) {
                    const val = (customer as any)[field];
                    if (val instanceof Date || (typeof val === 'string' && !isNaN(Date.parse(val)) && (val.includes('-') || val.includes('/')))) {
                        return formatDate(val);
                    }
                    return String(val ?? '');
                }
                return '';
            }
        }
    };

    const handlePrint = async () => {
        try {
            const printElement = document.getElementById('pawning-mandate-offscreen');
            if (!printElement) {
                toast.showError('Print element not found');
                return;
            }
            setPrinting(true);

            // Check latest print status from backend before printing
            let currentlyPrinted = isPrinted;
            try {
                const statusRes = await getPlAccountPrintStatus(accountId);
                currentlyPrinted = statusRes?.printed === 1;
                setIsPrinted(currentlyPrinted);
            } catch {
                // Use local state if the check fails
            }

            const pageWidth = printConfig?.page_width || '21cm';
            const pageHeight = printConfig?.page_height || '29.7cm';

            openPrintWindowHTML(printElement.outerHTML, pageWidth, pageHeight, 'Pawning Mandate', () => {
                // If this is the first print, mark as printed in the DB
                if (!currentlyPrinted && accountId) {
                    markPlAccountPrinted(accountId)
                        .then(() => { setIsPrinted(true); })
                        .catch(() => {
                            toast.showError('Warning: Could not save print status to server.');
                        });
                }
            });
            setPrinting(false);
        } catch {
            toast.showError('Failed to generate print');
            setPrinting(false);
        }
    };

    if (loading) {
        return <Loading message="Loading Pawning Mandate data..." />;
    }

    const hasConfig = printConfig && printConfig.required_fields && printConfig.required_fields.length > 0;

    // Get pawning item fields that need multi-row rendering
    const pawningItemFields = ['name', 'quantity', 'carat', 'weight_gram', 'gold_weight_gram', 'accessed_value', 'accessed_value_words', 'market_value', 'market_value_words', 'description'];
    const pawningItems = (account as any)?.pawning_items || [];

    const getActiveTables = () => {
        if (!printConfig?.sections) return [];
        return Object.keys(printConfig.sections)
            .filter(key => key === 'table' || key.startsWith('table_copy_'))
            .filter(key => {
                const sec = printConfig.sections[key];
                return sec && Object.keys(sec).length > 0 && sec.enabled !== false;
            });
    };

    const activeTables = getActiveTables();

    const renderTableSection = (tableKey: string) => {
        const tableConfig = printConfig?.sections?.[tableKey];
        if (!tableConfig || tableConfig.enabled === false) return null;

        const activeCols: string[] = tableConfig.columns || ["name", "description", "quantity", "carat", "weight_gram", "gold_weight_gram", "accessed_value", "market_value", "capital"];
        const currentLang = printLang || localStorage.getItem('app_lang') || 'en';

        const tableTop = tableConfig.top || "7.5cm";
        const tableLeft = tableConfig.left || "2cm";
        const tableWidth = tableConfig.width || "15cm";
        const rowHeight = tableConfig.row_height || "0.8cm";
        const hideHeader = tableConfig.hide_header === true || tableConfig.hide_header === "true";

        const getColWidth = (colId: string) => {
            const customWidth = tableConfig?.[`${colId}_width`];
            if (customWidth) return customWidth;

            // Fallback widths
            const fallbacks: Record<string, string> = {
                name: "20%",
                description: "15%",
                quantity: "10%",
                carat: "10%",
                weight_gram: "10%",
                gold_weight_gram: "10%",
                accessed_value: "12.5%",
                market_value: "12.5%",
                capital: "12.5%"
            };
            return fallbacks[colId] || "12.5%";
        };

        const getColStyles = (colId: string) => {
            const align = tableConfig?.[`${colId}_align`] ||
                (["quantity", "carat", "weight_gram", "gold_weight_gram", "accessed_value", "market_value", "capital"].includes(colId) ? "right" : "left");
            const fontSize = tableConfig?.[`${colId}_font_size`] || "inherit";
            const isBold = tableConfig?.[`${colId}_bold`] === true || tableConfig?.[`${colId}_bold`] === "true";
            const isItalic = tableConfig?.[`${colId}_italic`] === true || tableConfig?.[`${colId}_italic`] === "true";

            return {
                textAlign: align as any,
                fontSize,
                fontWeight: isBold ? 'bold' : 'normal',
                fontStyle: isItalic ? 'italic' : 'normal',
                padding: '4px',
                wordBreak: 'break-all' as any,
            };
        };

        return (
            <table
                key={tableKey}
                style={{
                    position: 'absolute',
                    top: tableTop,
                    left: tableLeft,
                    width: tableWidth,
                    borderCollapse: 'collapse',
                    fontSize: printConfig.font_size || '12px',
                    fontFamily: "'Noto Serif', 'Noto Serif Sinhala', 'Noto Sans Tamil', 'Times New Roman', 'Iskoola Pota', 'Latha', serif",
                    boxSizing: 'border-box',
                    zIndex: 2,
                }}
            >
                {!hideHeader && (
                    <thead>
                        <tr style={{ height: rowHeight }}>
                            {activeCols.map((colId) => {
                                const headerLabel = getFieldLabel(colId, currentLang, printConfig);
                                const styles = getColStyles(colId);
                                return (
                                    <th
                                        key={`th-${tableKey}-${colId}`}
                                        style={{
                                            ...styles,
                                            fontWeight: 'bold',
                                            backgroundColor: '#f8f9fa',
                                            width: getColWidth(colId),
                                        }}
                                    >
                                        {headerLabel}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {pawningItems.map((_item: any, itemIdx: number) => (
                        <tr key={`tr-${tableKey}-${itemIdx}`} style={{ height: rowHeight }}>
                            {activeCols.map((colId) => {
                                const styles = getColStyles(colId);
                                const val = getFieldValue(colId, isPrinted, itemIdx);
                                return (
                                    <td
                                        key={`td-${tableKey}-${itemIdx}-${colId}`}
                                        style={styles}
                                    >
                                        {val}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
                {!tableConfig?.hide_footer && (
                    <tfoot>
                        <tr style={{ height: rowHeight }}>
                            {activeCols.map((colId, idx) => {
                                const styles = getColStyles(colId);
                                let val = "";
                                if (colId === "quantity") {
                                    const total = pawningItems.reduce((sum: number, item: any) => sum + (Number(item.quantity) || 0), 0);
                                    val = total.toString();
                                } else if (colId === "weight_gram") {
                                    const total = pawningItems.reduce((sum: number, item: any) => sum + (Number(item.weight_gram) || 0), 0);
                                    val = total.toFixed(2);
                                } else if (colId === "gold_weight_gram") {
                                    const total = pawningItems.reduce((sum: number, item: any) => sum + (Number(item.gold_weight_gram) || 0), 0);
                                    val = total.toFixed(2);
                                } else if (colId === "accessed_value") {
                                    const total = pawningItems.reduce((sum: number, item: any) => sum + (Number(item.accessed_value) || 0), 0);
                                    val = formatCurrency(total);
                                } else if (colId === "market_value") {
                                    const total = pawningItems.reduce((sum: number, item: any) => sum + (Number(item.market_value) || 0), 0);
                                    val = formatCurrency(total);
                                } else if (colId === "capital") {
                                    const total = pawningItems.reduce((sum: number, item: any) => sum + (Number(item.capital) || 0), 0);
                                    val = formatCurrency(total);
                                } else if (idx === 0) {
                                    val = "Total:";
                                }

                                const isDoubleLine = tableConfig?.double_line_footer === true || tableConfig?.double_line_footer === "true";

                                return (
                                    <td
                                        key={`tf-${tableKey}-${colId}`}
                                        style={{
                                            ...styles,
                                            fontWeight: 'bold',
                                            borderTop: '1px solid #000',
                                            borderBottom: isDoubleLine ? '3px double #000' : 'none',
                                            paddingTop: '4px',
                                            paddingBottom: isDoubleLine ? '2px' : '0px',
                                        }}
                                    >
                                        {val}
                                    </td>
                                );
                            })}
                        </tr>
                    </tfoot>
                )}
            </table>
        );
    };

    return (
        <Box style={{ position: 'relative', width: '100%', overflow: 'auto' }}>
            {printConfigError && (
                <Box paddingBottom="medium">
                    <Alert tone="caution">
                        <Text size="small">{printConfigError}</Text>
                    </Alert>
                </Box>
            )}

            {hasConfig && (
                <>
                    {/* Calibration Toolbar */}
                    <Box className="no-print" paddingBottom="medium" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Text tone="secondary" size="small" weight="strong">
                            📐 Actual Page Size: {printConfig?.page_width || '21cm'} (Width) x {printConfig?.page_height || '29.7cm'} (Height)
                        </Text>
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {/* The Print Preview Canvas */}
                        <Box
                            style={{
                                border: '1px solid #adb5bd',
                                borderRadius: '8px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                                position: 'relative',
                                width: printConfig?.page_width || '21cm',
                                height: printConfig?.page_height || '29.7cm',
                                minWidth: printConfig?.page_width || '21cm',
                                minHeight: printConfig?.page_height || '29.7cm',
                                flexShrink: 0,
                                backgroundColor: '#fff',
                                fontFamily: "'Noto Serif', 'Noto Serif Sinhala', 'Noto Sans Tamil', 'Times New Roman', 'Iskoola Pota', 'Latha', serif",
                                fontSize: printConfig?.font_size || '12px',
                                overflow: 'hidden',
                                backgroundImage: printConfig?.background_image ? `url(${printConfig.background_image})` : undefined,
                                backgroundSize: '100% 100%',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                boxSizing: 'border-box',
                                paddingTop: printConfig?.margin_top || '0cm',
                                paddingBottom: printConfig?.margin_bottom || '0cm',
                                paddingLeft: printConfig?.margin_left || '0cm',
                                paddingRight: printConfig?.margin_right || '0cm',
                            }}
                        >
                            <Box style={{ position: 'absolute', top: '10px', left: '15px', opacity: 0.6, zIndex: 5 }} className="no-print">
                                <Text size="xsmall" tone="secondary" weight="strong">
                                    PAWNING MANDATE PREVIEW
                                </Text>
                            </Box>
                            {activeTables.map((tableKey) => renderTableSection(tableKey))}
                            {printConfig.required_fields.map((field: string) => {
                                const showLabel = Boolean(printConfig[`${field}_show_label`]);
                                const currentLang = printLang || localStorage.getItem('app_lang') || 'en';
                                const cleanField = field.replace(/_copy_\d+$/, "");
                                // Check if this is a pawning item field
                                if (pawningItemFields.includes(cleanField)) {
                                    // Render all pawning items for this field
                                    return pawningItems.map((_item: any, index: number) => {
                                        const value = getFieldValue(field, isPrinted, index);
                                        const baseTop = parseFloat((printConfig[`${field}_top`] || '1cm').replace('cm', ''));
                                        const lineHeight = 0.45; // 0.45cm spacing
                                        const top = baseTop + (index * lineHeight);
                                        return (
                                            <React.Fragment key={`preview-${field}-${index}`}>
                                                {showLabel && index === 0 && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            left: printConfig[`${field}_label_left`] || '0cm',
                                                            top: printConfig[`${field}_label_top`] || '0cm',
                                                            fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                            fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                            fontSize: printConfig[field]?.font_size || 'inherit',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {getFieldLabel(field, currentLang, printConfig)}
                                                    </div>
                                                )}
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: printConfig[`${field}_left`] || '0cm',
                                                        top: `${top}cm`,
                                                        fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                        fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                        fontSize: printConfig[field]?.font_size || 'inherit',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {value}
                                                </div>
                                            </React.Fragment>
                                        );
                                    });
                                }

                                // Regular field rendering
                                const value = getFieldValue(field, isPrinted);
                                return (
                                    <React.Fragment key={`preview-${field}`}>
                                        {showLabel && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: printConfig[`${field}_label_left`] || '0cm',
                                                    top: printConfig[`${field}_label_top`] || '0cm',
                                                    fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                    fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                    fontSize: printConfig[field]?.font_size || 'inherit',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {getFieldLabel(field, currentLang, printConfig)}
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: printConfig[`${field}_left`] || '0cm',
                                                top: printConfig[`${field}_top`] || '0cm',
                                                fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                fontSize: printConfig[field]?.font_size || 'inherit',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {value}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* Print Action */}
                    <Box className="no-print" style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 10, display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {subCategories.length > 0 && (
                            <select
                                value={selectedSubCategory}
                                onChange={e => handleSubCategoryChange(e.currentTarget.value)}
                                style={{
                                    padding: '4px 8px',
                                    fontSize: '13px',
                                    borderRadius: '4px',
                                    border: '1px solid #c8ccd4',
                                    backgroundColor: '#fff',
                                    color: '#2b303a',
                                    outline: 'none',
                                    cursor: 'pointer',
                                    height: '32px',
                                    fontWeight: 500,
                                }}
                            >
                                <option value="">Default</option>
                                {subCategories.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        )}
                        <select
                            value={printLang}
                            onChange={(e) => setPrintLang(e.target.value)}
                            style={{
                                padding: '4px 8px',
                                fontSize: '13px',
                                borderRadius: '4px',
                                border: '1px solid #c8ccd4',
                                backgroundColor: '#fff',
                                color: '#2b303a',
                                outline: 'none',
                                cursor: 'pointer',
                                height: '32px',
                                fontWeight: 500,
                            }}
                        >
                            <option value="en">English (EN)</option>
                            <option value="si">Sinhala (SI)</option>
                            <option value="ta">Tamil (TA)</option>
                        </select>
                        <Button
                            size="small"
                            tone={isPrinted ? 'critical' : 'brandAccent'}
                            icon={<IconPrint />}
                            onClick={handlePrint}
                            loading={printing}
                        >
                            {isPrinted ? 'Reprint Pawning Mandate' : 'Print Pawning Mandate'}
                        </Button>
                    </Box>
                </>
            )}

            {!hasConfig && !printConfigError && (
                <Alert tone="info">
                    <Stack space="small">
                        <Text weight="strong">No Configuration</Text>
                        <Text size="small">
                            Please configure Pawning Mandate layout in Print Policy Config before printing.
                        </Text>
                    </Stack>
                </Alert>
            )}

            {/* Off-screen Print Container */}
            {hasConfig && (
                <div style={{ position: 'absolute', top: '-10000px', left: '-10000px' }}>
                    <div
                        id="pawning-mandate-offscreen"
                        style={{
                            width: printConfig?.page_width || '21cm',
                            height: printConfig?.page_height || '29.7cm',
                            paddingTop: printConfig?.margin_top || '0cm',
                            paddingBottom: printConfig?.margin_bottom || '0cm',
                            paddingLeft: printConfig?.margin_left || '0cm',
                            paddingRight: printConfig?.margin_right || '0cm',
                            fontSize: printConfig?.font_size || '12px',
                            fontFamily: "'Noto Serif', 'Noto Serif Sinhala', 'Noto Sans Tamil', 'Times New Roman', 'Iskoola Pota', 'Latha', serif",
                            backgroundColor: '#ffffff',
                            position: 'relative',
                            boxSizing: 'border-box',
                            /* Do not include background image in the offscreen print container so background image does not print */
                            backgroundImage: undefined,
                            backgroundSize: '100% 100%',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            overflow: 'hidden'
                        }}
                    >
                        {activeTables.map((tableKey) => renderTableSection(tableKey))}
                        {printConfig.required_fields.map((field: string) => {
                            const showLabel = Boolean(printConfig[`${field}_show_label`]);
                            const currentLang = printLang || localStorage.getItem('app_lang') || 'en';
                            const cleanField = field.replace(/_copy_\d+$/, "");
                            // Check if this is a pawning item field
                            if (pawningItemFields.includes(cleanField)) {
                                // Render all pawning items for this field
                                return pawningItems.map((_item: any, index: number) => {
                                    const value = getFieldValue(field, isPrinted, index);
                                    const baseTop = parseFloat((printConfig[`${field}_top`] || '1cm').replace('cm', ''));
                                    const lineHeight = 0.45; // 0.45cm spacing
                                    const top = baseTop + (index * lineHeight);
                                    return (
                                        <React.Fragment key={`print-${field}-${index}`}>
                                            {showLabel && index === 0 && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: printConfig[`${field}_label_left`] || '0cm',
                                                        top: printConfig[`${field}_label_top`] || '0cm',
                                                        fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                        fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                        fontSize: printConfig[field]?.font_size || 'inherit',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getFieldLabel(field, currentLang, printConfig)}
                                                </div>
                                            )}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: printConfig[`${field}_left`] || '0cm',
                                                    top: `${top}cm`,
                                                    fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                    fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                    fontSize: printConfig[field]?.font_size || 'inherit',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {value}
                                            </div>
                                        </React.Fragment>
                                    );
                                });
                            }

                            // Regular field rendering
                            return (
                                <React.Fragment key={`print-${field}`}>
                                    {showLabel && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: printConfig[`${field}_label_left`] || '0cm',
                                                top: printConfig[`${field}_label_top`] || '0cm',
                                                fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                fontSize: printConfig[field]?.font_size || 'inherit',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {getFieldLabel(field, currentLang, printConfig)}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: printConfig[`${field}_left`] || '0cm',
                                            top: printConfig[`${field}_top`] || '0cm',
                                            fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                            fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                            fontSize: printConfig[field]?.font_size || 'inherit',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {getFieldValue(field, isPrinted)}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}
        </Box>
    );
};

export default PawningMandatePrint;
